import axios from "axios";
import { toast } from "react-toastify";

const API_URL = window._env_?.REACT_APP_API_URL || "";
const MAX_FILE_SIZE = window._env_?.REACT_APP_MAX_FILE_SIZE
  ? parseInt(window._env_?.REACT_APP_MAX_FILE_SIZE, 10)
  : 104 * 1024 * 1024; // fallback para 104MB
// 104 MB como fallback
// Captura a URL do backend a partir do `.env`

export interface FolderPermissionResponse {
  permissionId: number; // <-- Adicione isto
  folderPath: string;
  grantedOn: string;
  accessPermission: boolean;
}

interface Item {
  path: string;
  isDirectory: boolean;
  modifiedDate?: string;
  size?: string | null; // <-- Aqui está o ajuste
  accessPermission?: boolean;
}

export interface UserFoldersPermissionsResponse {
  userId: number;
  fullName: string;
  login: string;
  email: string;
  isGduUser: boolean;
  type: number;
  permissions: FolderPermissionResponse[];
}

export interface DeleteUserFromListRequest {
  AdminUsername: string;
  UsersToDelete: {
    Username: string;
    UserId: number;
  }[];
}

export interface DeleteUserFromListResponse {
  SuccessfullyDeletedUsers: string[];
  FailedToDeleteUsers: string[];
  Message: string;
}

export interface AuthResponse {
  token: string;
  user: {
    login: string;
    fullName: string;
    email: string;
    type: number;
    creationDate: string;
    tokens?: string[];
  };
}

export interface GetSharedFoldersAndFilesByUserRequest {
  username: string;
}

export interface DeleteFolderRequest {
  username: string;
  SourcePaths: string[]; // Aceita múltiplos caminhos
}

// Configuração do Axios
const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

export interface SharedItemResponse {
  path: string; // Caminho completo do item
  isDirectory: boolean; // Indica se é uma pasta
  modifiedDate?: string; // Data de modificação (opcional para compatibilidade)
  size?: string | null; // Tamanho do arquivo ou pasta, pode ser null
  accessPermission?: boolean; // Permissão de acesso, opcional
}

export interface GetAllFoldersForAdminRequest {
  username: string;
  path?: string; // Opcional para enviar um caminho específico
}

export interface FolderResponse {
  path: string;
  isDirectory: boolean;
  modifiedDate?: string;
  size?: string | null;
  hasChildren?: boolean; // Adicionada a propriedade
}

// Login do usuário
export const login = async (
  user: string,
  password: string,
  captchaToken: string
): Promise<AuthResponse> => {
  try {
    const response = await api.post<AuthResponse>("auth/login", {
      username: user,
      password: password,
      captchaToken: captchaToken, // ✅ alinhado com o backend
    });

    sessionStorage.setItem("username", response.data.user.login);

    return response.data;
  } catch (error: any) {
    if (error.response) {
      const status = error.response.status;

      if (status === 403) {
        throw new Error(
          "Acesso bloqueado. Verifique seu firewall ou permissões."
        );
      }

      const message =
        error.response.data?.message ||
        error.response.data ||
        "Erro ao realizar login.";

      throw new Error(message);
    }

    if (error.request) {
      throw new Error(
        "Não foi possível se conectar ao servidor. Verifique sua rede ou políticas de segurança (CORS/firewall)."
      );
    }

    throw new Error("Erro inesperado ao tentar realizar o login.");
  }
};

// Função para recuperar o token e configurar o Axios com ele
export const getAuthHeaders = () => {
  const token = sessionStorage.getItem("token");
  if (!token) throw new Error("Token não encontrado!");
  return { Authorization: `Bearer ${token}` };
};

export const getUserFoldersAndFiles = async (
  username: string
): Promise<SharedItemResponse[]> => {
  try {
    const requestData: GetSharedFoldersAndFilesByUserRequest = { username };
    const response = await api.post<SharedItemResponse[]>(
      "/filemanager/GetAllSharedFoldersByUser",
      requestData,
      {
        headers: getAuthHeaders(),
      }
    );

    return response.data.map((item) => ({
      path: item.path.replace(/\\\\/g, "\\"), // Corrige barras duplas
      isDirectory: item.isDirectory,
      modifiedDate: item.modifiedDate || undefined,
      size: item.isDirectory ? null : item.size || "0 B",
      accessPermission: item.accessPermission ?? false,
    }));
  } catch (error: any) {
    console.error(error.response?.data?.message);
    return [];
  }
};

export const createFolder = async (
  username: string,
  sourcePath: string,
  newFolderName: string
) => {
  try {
    const requestData = {
      username,
      sourcePath,
      newFolderName,
    };
    const response = await api.post("/filemanager/CreateFolder", requestData, {
      headers: getAuthHeaders(),
    });

    return response.data;
  } catch (error: any) {
    const errorMessage =
      error.response?.data || // Mensagem do backend
      error.response?.message || // Outra possibilidade
      error.message || // Mensagem genérica
      "Erro desconhecido ao criar a pasta.";
    throw new Error(errorMessage); // Repassa para ser tratado no `catch`
  }
};

export const deleteItems = async (username: string, SourcePaths: string[]) => {
  try {
    const requestData: DeleteFolderRequest = { username, SourcePaths };
    const response = await api.post("/filemanager/DeleteItems", requestData, {
      headers: getAuthHeaders(),
    });

    return response.data; // Resposta do servidor
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      "Erro desconhecido ao deletar os itens.";

    throw new Error(errorMessage);
  }
};

export const uploadFile = async (
  username: string,
  DestinationPath: string,
  files: File[]
) => {
  // Limite máximo em bytes (por padrão 100 MB se não definido)
  const maxFileSize = parseInt(
    (window as any)._env_?.REACT_APP_MAX_FILE_SIZE || MAX_FILE_SIZE,
    10
  );

  // Soma total dos arquivos
  const totalSize = files.reduce((sum, file) => sum + file.size, 0);

  if (totalSize > maxFileSize) {
    const msg = `A soma dos arquivos (${(totalSize / 1024 / 1024).toFixed(
      2
    )} MB) excede o limite de ${(maxFileSize / 1024 / 1024).toFixed(2)} MB.`;

    throw new Error(msg);
  }

  const formData = new FormData();
  formData.append("Username", username);
  formData.append("DestinationPath", DestinationPath);

  for (const file of files) {
    formData.append("File", file);
  }

  try {
    const response = await api.post("/filemanager/UploadFile", formData, {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "multipart/form-data",
      },
    });

    if (response.status === 200 || response.status === 201) {
      return response.data;
    }

    console.error("Resposta inesperada no upload de arquivos:", response);

    throw new Error("Erro inesperado no upload de arquivos.");
  } catch (err: any) {
    console.error("Erro no uploadFile:", err);

    if (axios.isAxiosError(err)) {
      const status = err.response?.status;

      if (status === 413) {
        const msg =
          "O arquivo ultrapassa o tamanho máximo permitido no servidor web.";

        throw new Error(msg);
      }

      if (!status && err.message === "Network Error" && err.request) {
        const msg =
          "Não foi possível enviar o arquivo. Ele pode ter ultrapassado o tamanho máximo permitido no servidor web.";

        throw new Error(msg);
      }

      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Erro ao enviar os arquivos.";

      throw new Error(errorMessage);
    }

    throw new Error("Erro desconhecido ao enviar os arquivos.");
  }
};

export const downloadFile = async (
  username: string,
  sourcePath: string
): Promise<Blob> => {
  const maxFileSize = parseInt(
    window._env_?.REACT_APP_MAX_FILE_SIZE || MAX_FILE_SIZE.toString(),
    10
  );

  try {
    // 👇 Obtém o diretório pai do arquivo
    const parentPath = sourcePath.substring(0, sourcePath.lastIndexOf("\\"));

    // 🔁 Consulta os arquivos na pasta pai
    const responseInfo = await getSharedFoldersByPath({
      username,
      path: parentPath,
    });

    const targetItem = responseInfo.find((item) => item.path === sourcePath);

    if (!targetItem || targetItem.isDirectory) {
      throw new Error("Item não encontrado ou é uma pasta.");
    }

    const sizeKB = parseFloat(targetItem.size || "0");
    const sizeBytes = sizeKB * 1024;

    if (sizeBytes > maxFileSize) {
      throw new Error(
        `O arquivo tem ${(sizeBytes / 1024 / 1024).toFixed(
          2
        )} MB e excede o limite de ${(maxFileSize / 1024 / 1024).toFixed(2)} MB.`
      );
    }

    // ✅ Download autorizado
    const fileResponse = await api.post(
      "filemanager/DownloadFile",
      { username, sourcePath },
      {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        responseType: "blob",
      }
    );

    return fileResponse.data;
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Erro ao realizar o download.";
    throw new Error(errorMessage);
  }
};

export const renameItem = async (
  username: string,
  sourcePath: string,
  newName: string
) => {
  try {
    const requestData = { username, sourcePath, newName };
    const response = await api.post("filemanager/RenameItem", requestData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      "Erro desconhecido ao renomear o item.";
    throw new Error(errorMessage);
  }
};

export const copyItems = async (
  username: string,
  sourcePaths: string[],
  destinationPath: string
) => {
  try {
    const requestData = { username, sourcePaths, destinationPath };
    const response = await api.post("/filemanager/CopyItems", requestData, {
      headers: getAuthHeaders(),
    });

    return response.data; // Resposta do servidor
  } catch (error: any) {
    console.error("Erro no copyItems:", error); // Inspeciona a estrutura do erro

    const errorMessage =
      error.response?.data?.message || // Verifica se a mensagem está no campo `message`
      error.response?.data || // Tenta capturar diretamente o payload de `data`
      error.message || // Captura a mensagem padrão de erro
      "Erro desconhecido ao copiar itens."; // Mensagem padrão

    throw new Error(errorMessage); // Repassa o erro com a mensagem correta
  }
};

export const moveItems = async (
  username: string,
  sourcePaths: string[],
  destinationPath: string
) => {
  const maxFileSize = parseInt(
    window._env_?.REACT_APP_MAX_FILE_SIZE || "104857600",
    10
  );

  const parentPath = sourcePaths[0]
    ? sourcePaths[0].substring(0, sourcePaths[0].lastIndexOf("\\"))
    : "";

  const responseInfo = await getSharedFoldersByPath({
    username,
    path: parentPath,
  });

  const selectedInfo = responseInfo.filter((item) =>
    sourcePaths.includes(item.path)
  );

  const totalBytes = selectedInfo
    .filter((item) => !item.isDirectory)
    .reduce((acc, item) => acc + parseFloat(item.size || "0") * 1024, 0);

  if (totalBytes > maxFileSize) {
    throw new Error(
      `Os arquivos somam ${(totalBytes / 1024 / 1024).toFixed(2)} MB e excedem o limite permitido.`
    );
  }

  // Continua normalmente
  const requestData = { username, sourcePaths, destinationPath };
  const response = await api.post("/filemanager/MoveItems", requestData, {
    headers: getAuthHeaders(),
  });

  return response.data;
};

export const zipItems = async (
  username: string,
  destinationPath: string,
  outputFileName: string,
  itemsToZip: string[]
) => {
  try {
    const requestData = {
      username,
      destinationPath,
      outputFileName,
      itemsToZip,
    };

    const response = await api.post("/filemanager/ZipItems", requestData, {
      headers: getAuthHeaders(),
    });

    return response.data; // Retorna a resposta do backend
  } catch (error: any) {
    const errorMessage =
      error.response?.data || // Mensagem retornada pelo backend
      error.message || // Mensagem genérica
      "Erro desconhecido ao deletar o usuário.";
    throw new Error(errorMessage);
  }
};

export const getAllUsers = async (username: string) => {
  try {
    const response = await api.get(
      `/user/GetAllUsersFromDB?username=${username}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data; // Lista de usuários retornada pelo backend
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || "Erro ao buscar usuários.";
    throw new Error(errorMessage);
  }
};

export const deleteUser = async (username: string, userToDelete: string) => {
  try {
    const response = await api.delete("/user/DeleteUser", {
      data: { username, userToDelete },
      headers: getAuthHeaders(),
    });

    return response.data; // Retorna o resultado da exclusão
  } catch (error: any) {
    // Verifica e retorna a mensagem de erro detalhada
    const errorMessage =
      error.response?.data || // Mensagem retornada pelo backend
      error.message || // Mensagem genérica
      "Erro desconhecido ao deletar o usuário.";
    throw new Error(errorMessage);
  }
};

export const addUser = async (username: string, userToAdd: string) => {
  try {
    const requestData = { username, userToAdd };
    const response = await api.post("/user/AddUser", requestData, {
      headers: getAuthHeaders(),
    });

    return response.data; // Retorna a resposta do backend
  } catch (error: any) {
    // Captura a mensagem de erro detalhada do backend
    const errorMessage =
      error.response?.data?.message || // Verifica se o erro está no campo `message`
      error.response?.data || // Captura o objeto de erro diretamente
      error.message || // Captura a mensagem padrão do Axios
      "Erro desconhecido ao adicionar o usuário."; // Mensagem genérica

    throw new Error(errorMessage); // Repassa o erro para o frontend
  }
};

export const getAllFoldersForAdmin = async (
  username: string,
  path: string = ""
): Promise<FolderResponse[]> => {
  try {
    const requestData: GetAllFoldersForAdminRequest = { username, path };
    const response = await api.post<FolderResponse[]>(
      "/filemanager/GetAllFoldersForAdmin",
      requestData,
      {
        headers: getAuthHeaders(),
      }
    );

    return response.data;
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || "Erro ao buscar pastas.";
    throw new Error(errorMessage);
  }
};

export const assignFolders = async (
  adminUsername: string,
  targetUsername: string,
  folderPermissions: { FolderPath: string; AccessPermission: boolean }[]
) => {
  try {
    const requestData = {
      AdminUsername: adminUsername,
      TargetUsername: targetUsername,
      FolderPermissions: folderPermissions, // Atualizado para enviar lista de objetos
    };

    const response = await api.post("/filemanager/assignFolders", requestData, {
      headers: getAuthHeaders(),
    });

    return response.data; // Retorna a resposta do backend
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      "Erro desconhecido ao adicionar permissões.";
    throw new Error(errorMessage);
  }
};

export const validatePath = async (
  username: string,
  path: string
): Promise<boolean> => {
  try {
    const requestData = { username, path };
    const response = await api.post("/filemanager/ValidatePath", requestData, {
      headers: getAuthHeaders(),
    });

    return true; // O caminho é válido
  } catch (error: any) {
    if (error.response?.status === 400 || error.response?.status === 403) {
      return false; // O caminho é inválido ou restrito
    }
    const errorMessage =
      error.response?.data?.message || "Erro ao validar o caminho.";
    throw new Error(errorMessage);
  }
};

export const removePermissions = async (
  adminUsername: string,
  targetUsername: string,
  folderPaths: string[]
) => {
  try {
    const requestData = {
      AdminUsername: adminUsername,
      TargetUsername: targetUsername,
      FolderPaths: folderPaths,
    };

    const response = await api.post(
      "/filemanager/DeletePermissions",
      requestData,
      {
        headers: getAuthHeaders(),
      }
    );

    return response.data; // Retorna a resposta do backend
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      "Erro desconhecido ao remover permissões.";
    throw new Error(errorMessage);
  }
};

// Importar usuários em massa usando arquivo XML
export const bulkInsertFromXml = async (username: string, xmlFile: File) => {
  const formData = new FormData();
  formData.append("Username", username);
  formData.append("XmlFile", xmlFile);

  try {
    const response = await api.post("/bulkuser/BulkInsertFromXml", formData, {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data; // Retorna a resposta do backend
  } catch (error: any) {
    // Garante que a mensagem e os detalhes do erro sejam retornados
    if (error.response?.data) {
      return error.response.data; // Retorna o corpo do erro diretamente
    }
    throw new Error(
      error.response?.data?.message ||
        error.message ||
        "Erro ao importar usuários."
    );
  }
};

export const bulkInsertFromXlsx = async (username: string, XLSXFile: File) => {
  const formData = new FormData();
  formData.append("Username", username);
  formData.append("XLSXFile", XLSXFile);

  try {
    const response = await api.post("/bulkuser/BulkInsertFromXlsx", formData, {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data; // Retorna a resposta do backend
  } catch (error: any) {
    // Garante que a mensagem e os detalhes do erro sejam retornados
    if (error.response?.data) {
      return error.response.data; // Retorna o corpo do erro diretamente
    }
    throw new Error(
      error.response?.data?.message ||
        error.message ||
        "Erro ao importar usuários."
    );
  }
};

export const getAllUsersAndFoldersPermissions = async (
  username: string
): Promise<UserFoldersPermissionsResponse[]> => {
  try {
    const response = await api.post<UserFoldersPermissionsResponse[]>(
      "/filemanager/GetAllFoldersAndPermissions",
      { AdminLogin: username }, // Enviar AdminLogin corretamente
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || "Erro ao buscar pastas e permissões.";
    throw new Error(errorMessage);
  }
};

export const deleteUserFromList = async (
  request: DeleteUserFromListRequest
): Promise<DeleteUserFromListResponse> => {
  try {
    const response = await api.post<DeleteUserFromListResponse>(
      "/user/DeleteUserFromList",
      request,
      {
        headers: getAuthHeaders(),
      }
    );

    // Exibe a mensagem completa da resposta no toast
    toast.success(response.data.Message || "Usuários deletados com sucesso!");

    return response.data;
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data ||
      error.message ||
      "Erro ao deletar usuários.";

    // Exibe o erro no toast
    toast.error(errorMessage);
    throw new Error(errorMessage);
  }
};

export const removePermissionsForUsers = async (
  adminUsername: string,
  usersPermissions: {
    TargetUsername: string;
    FolderPaths: string[];
  }[]
) => {
  try {
    const requestData = {
      AdminUsername: adminUsername, // Usuário logado
      UsersPermissions: usersPermissions, // Permissões por usuário e pastas
    };

    const response = await api.post(
      "/filemanager/DeleteUsersPermissions",
      requestData,
      {
        headers: getAuthHeaders(),
      }
    );

    return response.data; // Retorna a resposta do backend
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data ||
      error.message ||
      "Erro ao remover permissões.";
    throw new Error(errorMessage);
  }
};

export interface GetSharedFoldersByPathRequest {
  path: string;
  username: string;
}

export const getSharedFoldersByPath = async (
  request: GetSharedFoldersByPathRequest
): Promise<SharedItemResponse[]> => {
  try {
    const response = await api.post<SharedItemResponse[]>(
      "/filemanager/GetSharedFoldersByPath",
      request,
      {
        headers: getAuthHeaders(),
      }
    );

    const items = response.data.map((item) => ({
      path: item.path.replace(/\\\\/g, "\\"),
      isDirectory: item.isDirectory,
      modifiedDate: item.modifiedDate || undefined,
      size: item.isDirectory ? null : item.size || "0 B",
      accessPermission: item.accessPermission ?? false,
    }));

    return items;
  } catch (error: any) {
    const message =
      error.response?.data?.message ||
      error.response?.data ||
      error.message ||
      "Erro ao buscar conteúdo da pasta.";

    toast.error(`Erro ao buscar conteúdo: ${message}`);
    throw new Error(message);
  }
};

export const fetchFolderContents = async (
  path: string,
  username: string
): Promise<Item[]> => {
  try {
    const data = await getSharedFoldersByPath({ path, username });
    return data ?? []; // Garante que sempre volte um array
  } catch (error: any) {
    if (error.response?.status === 404) {
      return []; // Pasta está vazia, retorna array vazio sem logar erro
    }

    console.error("Erro ao buscar conteúdo da pasta:", error);
    return [];
  }
};

export const getUserByLogin = async (username: string) => {
  try {
    const response = await api.get(
      `/user/GetUserByLogin?username=${username}`,
      {
        headers: getAuthHeaders(),
      }
    );

    return response.data; // Retorna o objeto User vindo do backend
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data ||
      error.message ||
      "Erro ao buscar usuário por login.";

    console.error("Erro ao buscar usuário:", errorMessage);
    throw new Error(errorMessage);
  }
};

export default api;
